import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logEvent } from "@/hooks/use-event-log";
import { localDateOf, suggestWeekendHolidayHours } from "@/lib/overtime-calc";
import type { DBActiveTimer } from "@/lib/db-types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TimerStopCommentDialog } from "@/components/shared/TimerStopCommentDialog";

// ── Types ────────────────────────────────────────────────────

export interface StartTimerInput {
  taskId?: string | null;
  projectId: string;
  projectName: string;
  taskDescription?: string | null;
  phase?: string | null;
  budgetLineId?: string | null;
  assignmentId?: string | null;  // VIB-671
  isReisetid?: boolean;  // VIB-909
}

export interface ActiveTimer {
  taskId: string | null;
  projectId: string;
  projectName: string;
  taskDescription: string | null;
  phase: string | null;
  budgetLineId: string | null;
  assignmentId: string | null;  // VIB-671
  isReisetid?: boolean;  // VIB-909
  startedAt: number; // epoch ms
}

interface TimerContextValue {
  activeTimer: ActiveTimer | null;
  elapsedSeconds: number;
  getElapsed: () => number;
  startTimer: (input: StartTimerInput) => Promise<void>;
  stopTimer: () => Promise<void>;
  isRunning: boolean;
}

// ── Context ──────────────────────────────────────────────────

const TimerContext = createContext<TimerContextValue | null>(null);

interface PendingCommentState {
  timer: ActiveTimer;
  label: string;
  /** Called when user provides comment — saves and clears active timer */
  onSave: (comment: string, overnightCount: number | null) => Promise<void>;
  /** Called when user cancels — keeps timer running */
  onCancel: () => void;
}

export function TimerProvider({ children }: { children: React.ReactNode }) {
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [pendingStart, setPendingStart] = useState<StartTimerInput | null>(null);
  const [pendingComment, setPendingComment] = useState<PendingCommentState | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // VIB-882: re-entry-vern. stopTimer gjør et async insert før timeren
  // nullstilles; uten dette dobbeltlogger et dobbeltklikk/dobbel-tap samme økt.
  const stoppingRef = useRef(false);

  const { activeUser } = useImpersonation();
  const qc = useQueryClient();
  const userId = activeUser?.id ?? null;

  // ── Tick every second while a timer is running ────────────
  useEffect(() => {
    if (activeTimer) {
      setElapsedSeconds(Math.floor((Date.now() - activeTimer.startedAt) / 1000));
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - activeTimer.startedAt) / 1000));
      }, 1000);
    } else {
      setElapsedSeconds(0);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeTimer]);

  // ── Restore active timer on mount / user change ───────────
  useEffect(() => {
    if (!userId) {
      setActiveTimer(null);
      return;
    }

    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from("active_timers")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (cancelled) return;
      if (error) {
        console.error("TimerContext: failed to restore timer", error);
        return;
      }
      if (data) {
        setActiveTimer(rowToTimer(data as DBActiveTimer));
      } else {
        setActiveTimer(null);
      }
    })();

    // Realtime: sync across tabs/devices for same user
    const channel = supabase
      .channel(`active_timers:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "active_timers",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            setActiveTimer(null);
          } else {
            const row = payload.new as DBActiveTimer;
            setActiveTimer(rowToTimer(row));
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // ── Internal: persist new timer (upsert) ──────────────────
  const persistStart = useCallback(
    async (input: StartTimerInput): Promise<void> => {
      if (!userId) {
        toast.error("Kan ikke starte timer: ingen aktiv bruker.");
        return;
      }
      const startedAt = new Date();
      const { error } = await supabase.from("active_timers").upsert(
        {
          user_id: userId,
          project_id: input.projectId,
          project_name: input.projectName,
          task_id: input.taskId ?? null,
          task_description: input.taskDescription ?? null,
          phase: input.phase ?? null,
          budget_line_id: input.budgetLineId ?? null,
          assignment_id: input.assignmentId ?? null,  // VIB-671
          started_at: startedAt.toISOString(),
        },
        { onConflict: "user_id" },
      );

      if (error) {
        toast.error(`Kunne ikke starte timer: ${error.message}`);
        return;
      }

      setActiveTimer({
        taskId: input.taskId ?? null,
        projectId: input.projectId,
        projectName: input.projectName,
        taskDescription: input.taskDescription ?? null,
        phase: input.phase ?? null,
        budgetLineId: input.budgetLineId ?? null,
        assignmentId: input.assignmentId ?? null,  // VIB-671
        isReisetid: input.isReisetid ?? false,  // VIB-909
        startedAt: startedAt.getTime(),
      });
    },
    [userId],
  );

  // ── Public: startTimer with conflict-modal ───────────────
  const startTimer = useCallback(
    async (input: StartTimerInput): Promise<void> => {
      if (activeTimer) {
        // Already running — ask user to confirm replacement
        setPendingStart(input);
        return;
      }
      await persistStart(input);
    },
    [activeTimer, persistStart],
  );

  // ── Internal: clear active_timers row after successful save ──
  const clearActiveTimer = useCallback(async () => {
    if (!userId) return;
    const { error } = await supabase
      .from("active_timers")
      .delete()
      .eq("user_id", userId);
    if (error) {
      console.error("TimerContext: failed to clear active timer", error);
    }
    setActiveTimer(null);
  }, [userId]);

  // ── Public: stopTimer saves time_entry + deletes row ─────
  const stopTimer = useCallback(async (): Promise<void> => {
    if (!activeTimer || !userId) return;
    // VIB-882: et dobbeltklikk/dobbel-tap mens insertet er in-flight ville
    // ellers lagre samme økt to ganger (activeTimer er fortsatt satt til
    // clearActiveTimer kjører). Tidlig return hvis et stopp allerede pågår.
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    try {
      // <1 min forkastes bevisst (misklikk-vern): ikke lagret, timeren avsluttes,
      // og ingen kommentar-dialog — gjelder også interne prosjekter.
      const stopDuration = Math.floor((Date.now() - activeTimer.startedAt) / 1000);
      if (stopDuration < 60) {
        toast.info("Timer under 1 minutt — ikke lagret.");
        await clearActiveTimer();
        return;
      }

      // Kommentar kreves på interne OG T&M-prosjekter (VIB-790/791) og reisetid (VIB-909).
      const { data: projectData } = await supabase
        .from("projects")
        .select("is_internal, project_type")
        .eq("id", activeTimer.projectId)
        .single();
      const isReisetid = activeTimer.isReisetid === true;
      const requiresComment =
        projectData?.is_internal === true ||
        projectData?.project_type === "hourly" ||
        isReisetid;

      if (requiresComment) {
        const label = isReisetid
          ? "Reisetid (medgått)"
          : projectData?.is_internal
            ? "Internprosjekt"
            : "Timeprosjekt (T&M)";
        const reiseTimer = { ...activeTimer, isReisetid };
        // Show dialog — actual save happens in onSave callback. Egen
        // stoppingRef-guard der, siden lagringen skjer etter at denne
        // funksjonen (og dens finally) har returnert.
        setPendingComment({
          timer: reiseTimer,
          label,
          onSave: async (comment: string, overnightCount: number | null) => {
            if (stoppingRef.current) return;
            stoppingRef.current = true;
            try {
              setPendingComment(null);
              const saved = await saveTimerAsEntry(reiseTimer, userId, qc, comment, overnightCount);
              if (saved) {
                await clearActiveTimer();
              }
              // If !saved: timer stays running, error toast already shown.
            } finally {
              stoppingRef.current = false;
            }
          },
          onCancel: () => {
            setPendingComment(null);
            // Timer keeps running — no state change needed
          },
        });
        return;
      }

      const saved = await saveTimerAsEntry(activeTimer, userId, qc, null);
      if (saved) {
        await clearActiveTimer();
      }
      // If !saved: timer stays running, error toast already shown in saveTimerAsEntry
    } finally {
      stoppingRef.current = false;
    }
  }, [activeTimer, userId, qc, clearActiveTimer]);

  // ── Conflict-modal: confirm replacement ──────────────────
  const confirmPendingStart = useCallback(async () => {
    if (!pendingStart) return;
    // BUG-M3: speil stoppingRef-guarden fra stopTimer. Et dobbeltklikk på
    // «Bytt timer» mens den gamle timeren lagres in-flight ville ellers
    // dobbeltlagre samme time_entry (activeTimer er fortsatt satt til
    // saveTimerAsEntry/persistStart har kjørt ferdig).
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    try {
      if (activeTimer && userId) {
        // <1 min på den gamle timeren forkastes bevisst (misklikk-vern): ingen
        // kommentar-dialog, ingen lagring. persistStart overskriver den gamle
        // active_timers-raden, så bytte fungerer.
        const oldDuration = Math.floor((Date.now() - activeTimer.startedAt) / 1000);
        if (oldDuration < 60) {
          toast.info("Timer under 1 minutt — ikke lagret.");
        } else {
          // Kommentar kreves på interne OG T&M-prosjekter (VIB-790/791) og reisetid (VIB-909).
          const { data: projectData } = await supabase
            .from("projects")
            .select("is_internal, project_type")
            .eq("id", activeTimer.projectId)
            .single();
          const isReisetid = activeTimer.isReisetid === true;
          const requiresComment =
            projectData?.is_internal === true ||
            projectData?.project_type === "hourly" ||
            isReisetid;

          if (requiresComment) {
            const label = isReisetid
              ? "Reisetid (medgått)"
              : projectData?.is_internal
                ? "Internprosjekt"
                : "Timeprosjekt (T&M)";
            const reiseTimer = { ...activeTimer, isReisetid };
            const next = pendingStart;
            setPendingStart(null);
            // Saven skjer i onSave etter at denne funksjonen returnerer; frigi
            // topp-guarden her og la onSave eie sin egen guard (som i stopTimer).
            stoppingRef.current = false;
            setPendingComment({
              timer: reiseTimer,
              label,
              onSave: async (comment: string, overnightCount: number | null) => {
                if (stoppingRef.current) return;
                stoppingRef.current = true;
                try {
                  setPendingComment(null);
                  const saved = await saveTimerAsEntry(reiseTimer, userId, qc, comment, overnightCount);
                  if (saved) {
                    await persistStart(next);
                  }
                  // If !saved: old timer stays, error toast already shown
                } finally {
                  stoppingRef.current = false;
                }
              },
              onCancel: () => {
                setPendingComment(null);
                // Abandon the switch — keep old timer running
              },
            });
            return;
          }

          const saved = await saveTimerAsEntry(activeTimer, userId, qc, null);
          if (!saved) {
            // Old timer save failed — abort switch, keep old timer running
            setPendingStart(null);
            return;
          }
        }
      }

      const next = pendingStart;
      setPendingStart(null);
      await persistStart(next);
    } finally {
      // finally kjøres alltid, også etter `return` i comment-dialog-grenen —
      // der er guarden riktignok allerede frigitt (linje ~345), så denne
      // ekstra nullstillingen er harmløs/idempotent. For alle andre grener er
      // det her guarden faktisk frigis.
      stoppingRef.current = false;
    }
  }, [pendingStart, activeTimer, userId, qc, persistStart]);

  const cancelPendingStart = useCallback(() => {
    setPendingStart(null);
  }, []);

  const getElapsed = useCallback(() => {
    if (!activeTimer) return 0;
    return Math.floor((Date.now() - activeTimer.startedAt) / 1000);
  }, [activeTimer]);

  return (
    <TimerContext.Provider
      value={{
        activeTimer,
        elapsedSeconds,
        getElapsed,
        startTimer,
        stopTimer,
        isRunning: !!activeTimer,
      }}
    >
      {children}

      {pendingComment && (
        <TimerStopCommentDialog
          open
          projectName={pendingComment.timer.projectName}
          label={pendingComment.label}
          onSave={pendingComment.onSave}
          onCancel={pendingComment.onCancel}
        />
      )}

      <AlertDialog
        open={!!pendingStart}
        onOpenChange={(open) => {
          if (!open) cancelPendingStart();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bytte aktiv timer?</AlertDialogTitle>
            <AlertDialogDescription>
              {activeTimer ? (
                <>
                  Du har en timer som går på{" "}
                  <strong>{activeTimer.projectName}</strong>
                  {activeTimer.taskDescription ? (
                    <>
                      {" "}
                      ({activeTimer.taskDescription})
                    </>
                  ) : null}
                  . Hvis du starter ny timer på{" "}
                  <strong>{pendingStart?.projectName}</strong>
                  {pendingStart?.taskDescription ? (
                    <>
                      {" "}
                      ({pendingStart.taskDescription})
                    </>
                  ) : null}{" "}
                  blir den pågående timeren lagret som time entry først.
                </>
              ) : (
                <>Starte timer på {pendingStart?.projectName}?</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelPendingStart}>
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmPendingStart}>
              Bytt timer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TimerContext.Provider>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function rowToTimer(row: DBActiveTimer): ActiveTimer {
  return {
    taskId: row.task_id,
    projectId: row.project_id,
    projectName: row.project_name,
    taskDescription: row.task_description,
    phase: row.phase,
    budgetLineId: row.budget_line_id,
    assignmentId: row.assignment_id ?? null,  // VIB-671
    startedAt: new Date(row.started_at).getTime(),
  };
}

const LONG_TIMER_THRESHOLD_SECONDS = 36_000; // 10 hours

async function saveTimerAsEntry(
  timer: ActiveTimer,
  userId: string,
  qc: ReturnType<typeof useQueryClient>,
  comment: string | null,
  overnightCount: number | null = null,
): Promise<boolean> {
  const durationSeconds = Math.floor((Date.now() - timer.startedAt) / 1000);

  // Entries under 1 min forkastes bevisst (misklikk-vern) — IKKE lagret, men
  // timeren avsluttes så stopp/bytte fungerer. Returner true så caller får
  // rydde/bytte. (Kun ekte insert-feil under skal beholde timeren.)
  if (durationSeconds < 60) {
    toast.info("Timer under 1 minutt — ikke lagret.");
    return true;
  }

  // Warn (but still save) if the timer has been running for over 10 hours
  if (durationSeconds > LONG_TIMER_THRESHOLD_SECONDS) {
    const hours = Math.round(durationSeconds / 3600);
    toast.warning(
      `Timeren har gått i ${hours} timer. Kontroller at varigheten stemmer før du lagrer neste gang.`,
    );
  }

  const startDate = new Date(timer.startedAt).toISOString();
  const minutes = Math.round(durationSeconds / 60);
  const description =
    timer.taskDescription ??
    (timer.phase
      ? `${timer.projectName} - ${timer.phase}`
      : `Timer: ${timer.projectName}`);

  // VIB-909: reisetid logges fritt uten tildeling. Budget-linjen (og dynamisk
  // mersalg) opprettes/hentes on-demand via RPC før insert; assignment_id null.
  let blId = timer.budgetLineId ?? null;
  if (timer.isReisetid && !blId) {
    const { data, error } = await supabase.rpc("get_or_create_reisetid_line", {
      p_project_id: timer.projectId,
    });
    if (error) {
      toast.error("Kunne ikke opprette reisetid-linje");
      return false;
    }
    blId = data as string;
  }

  // VIB-1224: auto-sett helg-/helligdagstimer ved timer-stopp. Overstyring
  // skjer i RegistrationEditDialog etterpaa.
  const dateStr = localDateOf(startDate);
  const { data: holidayRow } = await supabase
    .from("company_holidays")
    .select("date")
    .eq("date", dateStr)
    .maybeSingle();
  const holidays = new Set<string>(holidayRow ? [holidayRow.date as string] : []);
  const weekendHolidayHours = suggestWeekendHolidayHours(startDate, durationSeconds, holidays);

  const { error } = await supabase.from("time_entries").insert({
    project_id: timer.projectId,
    user_id: userId,
    task_description: description,
    duration_seconds: durationSeconds,
    start_date: startDate,
    status: "registrert",
    comment,
    budget_line_id: blId,
    assignment_id: timer.isReisetid ? null : timer.assignmentId ?? null,  // VIB-671/909
    weekend_holiday_hours: weekendHolidayHours,
    overnight_count: overnightCount,  // VIB-1287 (fra timer-stopp-dialogen)
  });

  if (error) {
    toast.error(`Kunne ikke lagre timer: ${error.message}`);
    return false;
  }

  toast.success(`${minutes} min lagret for ${timer.projectName}`);
  logEvent({
    event_type: "time_entry_created",
    entity_type: "project",
    entity_id: timer.projectId,
    project_id: timer.projectId,
    user_id: userId,
    payload: {
      hours: Math.round((durationSeconds / 3600) * 100) / 100,
      task_description: description,
      duration_seconds: durationSeconds,
    },
  });
  await invalidateFor(qc, "time_entries", {
    userId,
    projectId: timer.projectId,
  });
  return true;
}

export function useTimer() {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error("useTimer must be used within TimerProvider");
  return ctx;
}
